// Componentes de ajuste pós-venda — modelo de pedido fantasma.
//
//   • AbrirAjusteModal     — modal de criação (motivo + obs + prazo)
//   • BotaoAbrirAjuste     — botão "Abrir ajuste" no detalhe do pedido ORIGINAL
//                            (cria pedido fantasma via onCriar)
//   • PainelAjusteFantasma — banner no detalhe do pedido FANTASMA com motivo,
//                            link pro original e data de abertura

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wrench, AlertCircle, Check, X, ExternalLink, Package } from 'lucide-react'
import { descricaoMovel } from '../utils/fmt'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { MOTIVOS_AJUSTE, labelMotivo, diasDoAjuste, hojeYmd } from '../data/ajustes'

function fmtData(iso) {
  if (!iso) return '—'
  try { return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) } catch { return iso }
}

const STATUS_PODE_ABRIR = new Set(['ENTREGUE', 'POS_VENDA'])

// ────────────────────────────────────────────────────────────────────────────
// Botão "Abrir ajuste" — no detalhe do pedido ORIGINAL
// ────────────────────────────────────────────────────────────────────────────
export function BotaoAbrirAjuste({ pedido, onCriar }) {
  const [modalAberto, setModalAberto] = useState(false)
  if (pedido?.tipoAjuste) return null
  if (!STATUS_PODE_ABRIR.has(pedido?.status)) return null

  return (
    <>
      <div className="card p-5 mb-4 border-amber-200 bg-amber-50/40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <Wrench size={18} className="text-amber-700" />
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm">Problema com esse pedido?</p>
              <p className="text-xs text-slate-500">
                Cria um pedido de ajuste (AJ-{pedido.numero}) que segue o fluxo normal —
                Cadastrado → Fabricando → Expedição → Rota → Entregue.
              </p>
            </div>
          </div>
          <button
            onClick={() => setModalAberto(true)}
            className="text-xs font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 px-3 py-2 rounded-lg flex items-center gap-1.5 transition-colors"
          >
            <AlertCircle size={13} />
            Abrir ajuste
          </button>
        </div>
      </div>
      {modalAberto && (
        <AbrirAjusteModal
          pedido={pedido}
          onClose={() => setModalAberto(false)}
          onConfirm={dados => { onCriar?.(dados); setModalAberto(false) }}
        />
      )}
    </>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Banner do pedido fantasma — informativo, sem botão de avançar
// ────────────────────────────────────────────────────────────────────────────
export function PainelAjusteFantasma({ pedido }) {
  const navigate = useNavigate()
  if (!pedido?.tipoAjuste) return null

  return (
    <div className="card p-5 mb-4 border-2 border-amber-300 bg-amber-50/40">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Wrench size={16} className="text-amber-700" />
          <span className="px-2 py-1 rounded-md text-[10px] font-black uppercase tracking-wide bg-amber-200 text-amber-900">
            🔧 AJUSTE
          </span>
          <span className="text-sm font-bold text-slate-800">
            Pedido de ajuste · AJ-{pedido.pedidoOriginalNumero || ''}
          </span>
          <span className="text-xs text-slate-500">
            · {labelMotivo(pedido.motivoAjuste)} · {diasDoAjuste(pedido)} dia(s)
          </span>
        </div>
        {pedido.pedidoOriginalId && (
          <button
            onClick={() => navigate(`/pedidos/${pedido.pedidoOriginalId}`)}
            className="text-xs font-semibold text-[#CC8800] hover:underline flex items-center gap-1"
            title="Abrir pedido original"
          >
            <ExternalLink size={11} />
            Pedido original #{pedido.pedidoOriginalNumero}
          </button>
        )}
      </div>

      {/* Obs por item (preferência) — só itens que têm observacaoAjuste */}
      {(() => {
        const moveisComObs = (pedido.moveis || []).filter(m => m?.observacaoAjuste?.trim())
        if (moveisComObs.length > 0) {
          return (
            <div className="bg-white/60 rounded-lg px-3 py-2 mb-2 space-y-1">
              {moveisComObs.map((m, i) => (
                <div key={i} className="text-[12px] text-slate-700">
                  <span className="font-semibold text-slate-800">{m.modelo || 'Móvel'}:</span>{' '}
                  <span className="italic">{m.observacaoAjuste}</span>
                </div>
              ))}
            </div>
          )
        }
        // Fallback: obs geral (caso pedido não tinha móveis estruturados)
        if (pedido.observacaoAjuste) {
          return (
            <p className="text-sm text-slate-700 bg-white/60 rounded-lg px-3 py-2 mb-2 italic">
              {pedido.observacaoAjuste}
            </p>
          )
        }
        return null
      })()}

      <p className="text-[11px] text-slate-500">
        Aberto em {fmtData(pedido.dataAbertura)} — segue o fluxo padrão do Kanban
        (mude o status acima ou na Logística como qualquer pedido).
      </p>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// Modal de abertura
// ────────────────────────────────────────────────────────────────────────────
export function AbrirAjusteModal({ pedido, onClose, onConfirm }) {
  const [motivo, setMotivo]         = useState(MOTIVOS_AJUSTE[0].id)
  const [prazo, setPrazo]           = useState(hojeYmd())

  // Lista de móveis do pedido (se houver) com checkbox + obs específica por item.
  // Default: todos marcados — gerente desmarca os que não precisam de ajuste e
  // detalha o problema item a item.
  const moveisOrig = Array.isArray(pedido?.moveis) ? pedido.moveis : []
  const [moveisSel, setMoveisSel] = useState(() => {
    const init = {}
    moveisOrig.forEach((m, i) => { init[m.id || i] = true })
    return init
  })
  // Observação por móvel — chave = m.id (ou índice se sem id)
  const [obsPorMovel, setObsPorMovel] = useState({})
  // Observação extra opcional pra pedidos sem móveis estruturados (Bling).
  // Só aparece quando o pedido NÃO tem moveis (fallback).
  const [observacaoGeral, setObservacaoGeral] = useState('')

  function toggleMovel(key) {
    setMoveisSel(prev => ({ ...prev, [key]: !prev[key] }))
  }
  function updateObs(key, valor) {
    setObsPorMovel(prev => ({ ...prev, [key]: valor }))
  }

  const moveisSelArray = moveisOrig.filter((m, i) => moveisSel[m.id || i])
  const quantosSelecionados = moveisSelArray.length
  const podeConfirmar = !!prazo && (moveisOrig.length === 0 || quantosSelecionados > 0)

  function confirmar() {
    if (!podeConfirmar) return
    // Cada móvel selecionado leva sua obs específica embarcada
    const moveisSelecionados = moveisOrig
      .filter((m, i) => moveisSel[m.id || i])
      .map((m, idxOrig) => {
        const i = moveisOrig.indexOf(m)
        const key = m.id || i
        return { ...m, observacaoAjuste: (obsPorMovel[key] || '').trim() }
      })
    // Observação consolidada do pedido AJ — concatena as obs por item (pra
    // listagens/banner). Fallback pra observacaoGeral quando não há moveis.
    const partes = moveisSelecionados
      .map(m => m.observacaoAjuste ? `• ${m.modelo || 'Móvel'}: ${m.observacaoAjuste}` : null)
      .filter(Boolean)
    const observacao = partes.length > 0 ? partes.join('\n') : observacaoGeral.trim()
    onConfirm({ motivo, observacao, prazo, moveisSelecionados })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Wrench size={16} className="text-amber-600" />
            Abrir ajuste
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
          <p className="text-xs text-slate-500">
            Cria um <strong>pedido de ajuste novo</strong> (código <strong>AJ-</strong>) que aparece
            no <strong>Kanban</strong> e na <strong>Logística</strong> como qualquer outro pedido,
            seguindo o fluxo padrão. O pedido original fica intocado em ENTREGUE.
          </p>

          {/* Lista de móveis com checkbox + obs específica por item */}
          {moveisOrig.length > 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-2 flex items-center gap-1.5">
                <Package size={11} />
                Quais móveis precisam de ajuste? <span className="text-slate-400 font-normal">({quantosSelecionados}/{moveisOrig.length})</span>
              </label>
              <div className="space-y-2 bg-slate-50 rounded-lg p-2 border border-slate-200">
                {moveisOrig.map((m, i) => {
                  const key = m.id || i
                  const ativo = !!moveisSel[key]
                  return (
                    <div
                      key={key}
                      className={`rounded-md border transition-colors overflow-hidden
                        ${ativo
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-white border-slate-200'}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleMovel(key)}
                        className="w-full flex items-start gap-2 px-2.5 py-2 text-left hover:bg-black/[0.02] transition-colors"
                      >
                        <span className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors
                          ${ativo
                            ? 'bg-amber-600 border-amber-600 text-white'
                            : 'bg-white border-slate-300'}`}>
                          {ativo && <Check size={10} strokeWidth={3} />}
                        </span>
                        <span className={`text-xs leading-snug flex-1 ${ativo ? 'text-slate-800 font-medium' : 'text-slate-500'}`}>
                          {descricaoMovel(m)}
                        </span>
                      </button>
                      {/* Observação específica desse móvel — só aparece quando marcado */}
                      {ativo && (
                        <div className="px-2.5 pb-2 pt-0">
                          <input
                            type="text"
                            value={obsPorMovel[key] || ''}
                            onChange={e => updateObs(key, e.target.value)}
                            placeholder="Problema específico (ex: porta empenou, pé soltou)"
                            className="w-full text-[11px] text-slate-700 bg-white border border-amber-200 rounded px-2 py-1 outline-none focus:border-amber-500"
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              {quantosSelecionados === 0 && (
                <p className="text-[10px] text-red-600 mt-1.5 italic">
                  Selecione pelo menos 1 móvel
                </p>
              )}
            </div>
          )}

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">Motivo</label>
            <select
              value={motivo} onChange={e => setMotivo(e.target.value)}
              className="input w-full text-sm"
            >
              {MOTIVOS_AJUSTE.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1.5">
              Prazo previsto da reentrega
            </label>
            <input
              type="date" value={prazo}
              onChange={e => setPrazo(e.target.value)}
              className="input w-full text-sm"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Pode ajustar depois arrastando o pedido na Logística.
            </p>
          </div>

          {/* Observação geral — só aparece quando o pedido NÃO tem móveis
              estruturados (Bling sem detalhamento). Caso normal usa obs por item. */}
          {moveisOrig.length === 0 && (
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1.5">
                Observação <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <textarea
                value={observacaoGeral} onChange={e => setObservacaoGeral(e.target.value)}
                placeholder="Detalhe o problema, peças afetadas, combinado com o cliente…"
                className="input w-full text-sm min-h-[70px] resize-y"
              />
            </div>
          )}
        </div>

        <div className="flex gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50 shrink-0">
          <button onClick={onClose} className="btn-secondary text-sm flex-1 justify-center">
            Cancelar
          </button>
          <button
            onClick={confirmar}
            className="btn-primary text-sm flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!podeConfirmar}
          >
            <Check size={13} />
            Criar pedido de ajuste
          </button>
        </div>
      </div>
    </div>
  )
}
